/**
 * LayoutPicker
 *
 * Visual selector for plays-per-slide layout (1, 2, 4, or 6).
 * Each option shows a miniature grid preview of the slide layout.
 */

import type { SlideLayout } from '@/types/SlideExport.types'

interface LayoutPickerProps {
	layout: SlideLayout
	onLayoutChange: (layout: SlideLayout) => void
}

const LAYOUTS: { value: SlideLayout; label: string; description: string }[] = [
	{ value: 1, label: '1', description: 'Full slide' },
	{ value: 2, label: '2', description: 'Side by side' },
	{ value: 4, label: '4', description: '2×2 grid' },
	{ value: 6, label: '6', description: '3×2 grid' },
]

export function LayoutPicker({ layout, onLayoutChange }: LayoutPickerProps) {
	return (
		<div className="layout-picker">
			<span className="layout-picker__label">Plays per slide</span>
			<div className="layout-picker__options">
				{LAYOUTS.map((option) => (
					<button
						key={option.value}
						className="layout-picker__option"
						data-selected={layout === option.value}
						onClick={() => onLayoutChange(option.value)}
						title={option.description}
					>
						<LayoutIcon count={option.value} />
						<span className="layout-picker__count">{option.label}</span>
					</button>
				))}
			</div>
		</div>
	)
}

/** Miniature SVG icon showing the grid layout for a given play count */
function LayoutIcon({ count }: { count: SlideLayout }) {
	const gap = 2
	const w = 40
	const h = 28

	const rects = getLayoutRects(count, w, h, gap)

	return (
		<svg
			viewBox={`0 0 ${w} ${h}`}
			className="layout-picker__icon"
			aria-hidden="true"
		>
			<rect
				width={w}
				height={h}
				rx={2}
				fill="var(--secondary)"
				stroke="var(--border)"
				strokeWidth={1}
			/>
			{rects.map((r, i) => (
				<rect
					key={i}
					x={r.x}
					y={r.y}
					width={r.w}
					height={r.h}
					rx={1}
					fill="var(--muted-foreground)"
					opacity={0.3}
				/>
			))}
		</svg>
	)
}

function getLayoutRects(
	count: SlideLayout,
	totalW: number,
	totalH: number,
	gap: number,
): { x: number; y: number; w: number; h: number }[] {
	const pad = 3

	if (count === 1) {
		return [{ x: pad, y: pad, w: totalW - pad * 2, h: totalH - pad * 2 }]
	}

	if (count === 2) {
		const slotW = (totalW - pad * 2 - gap) / 2
		return [
			{ x: pad, y: pad, w: slotW, h: totalH - pad * 2 },
			{ x: pad + slotW + gap, y: pad, w: slotW, h: totalH - pad * 2 },
		]
	}

	if (count === 4) {
		const slotW = (totalW - pad * 2 - gap) / 2
		const slotH = (totalH - pad * 2 - gap) / 2
		return [
			{ x: pad, y: pad, w: slotW, h: slotH },
			{ x: pad + slotW + gap, y: pad, w: slotW, h: slotH },
			{ x: pad, y: pad + slotH + gap, w: slotW, h: slotH },
			{ x: pad + slotW + gap, y: pad + slotH + gap, w: slotW, h: slotH },
		]
	}

	// 6: 3×2 grid
	const cols = 3
	const rows = 2
	const slotW = (totalW - pad * 2 - gap * (cols - 1)) / cols
	const slotH = (totalH - pad * 2 - gap * (rows - 1)) / rows
	const rects: { x: number; y: number; w: number; h: number }[] = []
	for (let r = 0; r < rows; r++) {
		for (let c = 0; c < cols; c++) {
			rects.push({
				x: pad + c * (slotW + gap),
				y: pad + r * (slotH + gap),
				w: slotW,
				h: slotH,
			})
		}
	}
	return rects
}
